/**
 * The `#282` wire error codes and the **German** sentence each one becomes on screen.
 *
 * Two rules this module exists to keep, both of which are easy to break by writing the message at the call site:
 *
 * 1. **Never render the backend's `description`.** It is English, written for an operator reading a log
 *    (`"The verification code is incorrect or has already been used."`), and the app has no i18n layer — a
 *    German screen that suddenly speaks English is worse than a generic German sentence. `ErrorCatalog` is also
 *    free to reword a description at any time; it is documentation, not UI copy.
 * 2. **One mapping, three surfaces.** The profile panel, the login code step and the remediation screen can all
 *    receive the same code, and a per-surface copy is a copy that drifts — the two-factor codes were designed
 *    to be told apart (`CODE_INCORRECT` vs `LOCKED` vs `CHALLENGE_INVALID` mean three different next actions),
 *    and that distinction is only worth anything if every screen honours it.
 *
 * Addressing is the formal *Sie*, matching the surfaces this appears on (profile, login) rather than the
 * employee panel's *du*.
 */

import {SecondFactorChallenge} from './two-factor.models';

/**
 * The `#282` codes, exactly as `API.Core/Authorization/AuthErrorCodes.cs` registers them.
 *
 * Spelled out as literals rather than derived from anything: these strings are the contract, and a typo here is
 * a branch that silently never fires. Grouped so a reviewer can diff them against the C# section by eye.
 */
export const SecondFactorErrorCodes = {
  /** The password was right and a second factor is owed. Carries a `SecondFactorChallenge` in `details`. */
  Required: 'E_AUTH_TWO_FACTOR_REQUIRED',
  /** Wrong, stale, or already used — deliberately ONE code for all three (see the C# remarks). */
  CodeIncorrect: 'E_AUTH_TWO_FACTOR_CODE_INCORRECT',
  /** Too many wrong codes; the factor is refusing attempts for a while. */
  Locked: 'E_AUTH_TWO_FACTOR_LOCKED',
  /** The challenge is missing, expired, or belongs to a different account. */
  ChallengeInvalid: 'E_AUTH_TWO_FACTOR_CHALLENGE_INVALID',
  /** An action needing an enrolled factor was asked of an account that has none. */
  NotEnrolled: 'E_AUTH_TWO_FACTOR_NOT_ENROLLED',
  /** Enrolment was confirmed for an account that already had a factor. */
  AlreadyEnrolled: 'E_AUTH_TWO_FACTOR_ALREADY_ENROLLED',
  /** Removal refused because the company requires two factors. */
  RemovalBlocked: 'E_AUTH_TWO_FACTOR_REMOVAL_BLOCKED',
  /** The company demands a federated sign-in, which a password account cannot provide by itself. */
  LevelUnreachable: 'E_AUTH_ACCOUNT_SECURITY_LEVEL_UNREACHABLE',
  /**
   * The company requires a second factor this account has not enrolled.
   *
   * Two different situations answer with it, and the screen has to tell them apart:
   *
   * - from the **middleware**, on a restricted password session (#282): every request but the enrolment ones is
   *   refused, and the app is already on the remediation screen;
   * - from **entering a context** (#279): the session is NOT restricted — it is an ordinary contextless one that
   *   already reaches the enrolment endpoints — and it was this one entry that was refused. The chooser
   *   navigates to the remediation screen itself, because the remedy is a whole screen rather than a sentence.
   */
  Restricted: 'E_AUTH_ACCOUNT_SECURITY_RESTRICTED',
  /**
   * The session asked to be re-issued after enrolling and carries no restriction for a re-mint to lift.
   *
   * Answered to any session with no `acct_sec` claim — a native account's, a federated one's, an employee
   * context entered from either. Such a session is never restricted: its company's requirement is applied when
   * a context is *entered*, where failing it is a refusal rather than a narrowed session, and it is re-read
   * server-side at the next entry.
   *
   * The remediation screen already skips the reissue for exactly this case, so reaching this code means that
   * guard did not fire. Mapped anyway: the fallback sentence says "bitte versuchen Sie es erneut", and retrying
   * is the one thing that can never work here.
   */
  ReissueNotApplicable: 'E_AUTH_SECOND_FACTOR_REISSUE_NOT_APPLICABLE'
} as const;

export type SecondFactorErrorCode = typeof SecondFactorErrorCodes[keyof typeof SecondFactorErrorCodes];

/** The German fallback — every unmapped code, every network failure, every response with no code at all. */
export const SECOND_FACTOR_FALLBACK_MESSAGE =
  'Die Aktion konnte nicht ausgeführt werden. Bitte versuchen Sie es erneut.';

/**
 * Appended to the lock-out message wherever a recovery code is genuinely an option.
 *
 * The lock is **TOTP-only**: a correct app code stays refused for its duration, but a recovery code is accepted
 * and clears the lock (a 98-bit code is not guessable, and five wrong codes is overwhelmingly a drifted phone
 * clock rather than an attack). That asymmetry is invisible from outside, so without saying it somebody sits
 * and waits while the one thing that would work is on the screen in front of them.
 *
 * Kept OUT of the `Locked` message itself and appended conditionally, because it is only true *sometimes*: not
 * during an enrolment (no codes have been issued yet), not for an account whose codes are all spent, and not
 * for somebody already typing one. Promising a route that cannot work is worse than saying nothing.
 *
 * Names **no duration**, matching the `Locked` message it extends — see the note there.
 */
export const SECOND_FACTOR_LOCKED_RECOVERY_HINT =
  'Ein Wiederherstellungscode wird trotzdem akzeptiert und hebt die Sperre auf.';

/**
 * The sentence each code becomes. Every one names the **next action**, because that is the only thing the
 * reader can do with it — "falsch" alone does not distinguish "type it again" from "wait" from "start over".
 */
const MESSAGES: Record<string, string> = {
  [SecondFactorErrorCodes.CodeIncorrect]:
    'Der Code ist falsch oder wurde bereits verwendet. Bitte geben Sie den aktuell angezeigten Code aus Ihrer '
    + 'Authenticator-App ein.',
  // Deliberately names NO duration ("einen Moment", not "15 Minuten"): the lock window is a server-side policy
  // the SPA does not read, and a wrong promise is worse than a vague one. Do not "improve" it with a number.
  // Where a recovery code is genuinely available, SECOND_FACTOR_LOCKED_RECOVERY_HINT is appended to this.
  [SecondFactorErrorCodes.Locked]:
    'Zu viele falsche Codes. Die Anmeldung ist vorübergehend gesperrt — bitte warten Sie einen Moment und '
    + 'versuchen Sie es dann erneut.',
  [SecondFactorErrorCodes.ChallengeInvalid]:
    'Die Anmeldung ist abgelaufen. Bitte melden Sie sich erneut mit Benutzername und Passwort an.',
  [SecondFactorErrorCodes.NotEnrolled]:
    'Für dieses Konto ist keine Authenticator-App eingerichtet.',
  [SecondFactorErrorCodes.AlreadyEnrolled]:
    'Für dieses Konto ist bereits eine Authenticator-App eingerichtet. Bitte laden Sie die Seite neu.',
  [SecondFactorErrorCodes.RemovalBlocked]:
    'Ihr Unternehmen verlangt die Zwei-Faktor-Authentifizierung. Sie kann deshalb nicht entfernt werden.',
  [SecondFactorErrorCodes.LevelUnreachable]:
    'Ihr Unternehmen verlangt die Anmeldung über einen externen Anbieter (z. B. Microsoft). Dieses Konto kann '
    + 'das nicht leisten — bitte wenden Sie sich an Ihre Administration.',
  [SecondFactorErrorCodes.Required]:
    'Für dieses Konto ist ein zweiter Faktor erforderlich.',
  // Names the next action, like every other entry, and that action is NOT "try again": there is nothing about
  // this session for a repeat to change. The enrolment itself succeeded, so the sentence says so first —
  // otherwise it reads as though the authenticator had not been set up.
  [SecondFactorErrorCodes.ReissueNotApplicable]:
    'Die Authenticator-App ist eingerichtet. Für diese Sitzung gibt es nichts freizuschalten — bitte wählen '
    + 'Sie einen Arbeitsbereich aus, die Anforderung wird beim Eintritt geprüft.'
};

/**
 * The `code` of a structured {@link ApiError} response, or null when the failure carries none (a network error,
 * a proxy's HTML error page, an older server).
 *
 * Reads `err.error.code` — the shape `ResponseModel<T>.BuildResponse()` emits. Written defensively because this
 * is fed straight from an rxjs `error` callback, whose argument is genuinely `unknown`.
 */
export function readErrorCode(err: unknown): string | null {
  const code = (err as {error?: {code?: unknown}} | null)?.error?.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
}

/** Extra context a surface can supply so the message names an action that is actually open to this reader. */
export interface SecondFactorMessageContext {
  /**
   * Whether a **recovery code** is a route this reader can take right now — the account has unused codes, the
   * action accepts one, and they are not already typing one.
   *
   * Only consulted for a lock-out. The caller owns this decision because only it knows all three parts: the
   * panel reads `remainingRecoveryCodes` and its own mode, the login screen reads the challenge's
   * `recoveryCodesAvailable`. Defaulting it to `true` here would put the hint on the enrolment step, where no
   * recovery codes exist at all.
   */
  recoveryCodeAvailable?: boolean;
}

/**
 * The German sentence for a failed second-factor call.
 *
 * @param err       whatever rxjs handed the `error` callback.
 * @param fallback  the sentence for an unrecognised failure. Override it where the surface can say something
 *                  more specific about what was being attempted ("Der Code konnte nicht geprüft werden.").
 * @param context   what the reader can actually do about it — see {@link SecondFactorMessageContext}.
 */
export function describeSecondFactorError(err: unknown,
                                          fallback: string = SECOND_FACTOR_FALLBACK_MESSAGE,
                                          context: SecondFactorMessageContext = {}): string {
  const code = readErrorCode(err);
  const message = (code && MESSAGES[code]) || fallback;

  // The lock is TOTP-only, so during it a recovery code is not merely *an* option — it is the only one. Saying
  // so belongs here rather than at each call site, or the three surfaces would word the same fact differently.
  return code === SecondFactorErrorCodes.Locked && context.recoveryCodeAvailable
    ? `${message} ${SECOND_FACTOR_LOCKED_RECOVERY_HINT}`
    : message;
}

/** Whether a failure is the specific code given — the readable form of the `readErrorCode(err) === X` branch. */
export function isSecondFactorError(err: unknown, code: SecondFactorErrorCode): boolean {
  return readErrorCode(err) === code;
}

/**
 * The {@link SecondFactorChallenge} riding a refused login's `ApiError.details`, or null when this failure is
 * not a two-factor challenge.
 *
 * Both halves are checked, not just the code: `details` is `object?` on the server and omitted from the wire
 * entirely when null (see `ApiError`), so a build that answers the code without the payload is a real
 * possibility. Returning null there is what makes the login fall back to its generic message instead of moving
 * to a code step it can never complete — a challenge-less code step looks like a bug in the *user's* typing.
 */
export function readSecondFactorChallenge(err: unknown): SecondFactorChallenge | null {
  if (!isSecondFactorError(err, SecondFactorErrorCodes.Required)) return null;

  const details = (err as {error?: {details?: unknown}} | null)?.error?.details as
    Partial<SecondFactorChallenge> | undefined;
  if (!details || typeof details.challenge !== 'string' || details.challenge.length === 0) return null;

  return {
    challenge: details.challenge,
    expiresUtc: typeof details.expiresUtc === 'string' ? details.expiresUtc : '',
    // Absent ⇒ do not offer the recovery route. Guessing `true` would show a way in that answers every code
    // with "incorrect", which reads as "my printout is wrong" rather than "there is nothing to type here".
    recoveryCodesAvailable: details.recoveryCodesAvailable === true
  };
}
